import React, { useState } from 'react';
import {
  Box, Container, Grid, Card, CardContent, Typography, Chip,
  TextField, MenuItem, InputAdornment, Stack, Button, Divider, Paper,
} from '@mui/material';
import { Search, CalendarToday, MedicalServices, ArrowForward, CalendarMonth } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { newsItems } from '../data/hospitalData';
import PageHero from '../components/common/PageHero';

const categories = ['Announcement', 'Campaign', 'Health Tip', 'Vaccination', 'Awareness', 'Prevention'];

const categoryColors: Record<string, string> = {
  Announcement: '#0B4F8A',
  Campaign: '#2E7D32',
  'Health Tip': '#F57C00',
  Vaccination: '#6A1B9A',
  Awareness: '#C62828',
  Prevention: '#00695C',
};

const subCards = [
  { icon: '📢', titleEn: 'Hospital Announcements', titleFr: 'Annonces Hospitalières', bgColor: '#0B4F8A' },
  { icon: '🏥', titleEn: 'Health Campaigns', titleFr: 'Campagnes Santé', bgColor: '#2E7D32' },
  { icon: '💡', titleEn: 'Health Tips & Prevention', titleFr: 'Conseils Santé & Prévention', bgColor: '#F57C00' },
  { icon: '💉', titleEn: 'Vaccination News', titleFr: 'Actualités Vaccination', bgColor: '#6A1B9A' },
];

const NewsPage: React.FC = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');

  const filtered = newsItems.filter((item) => {
    const title = language === 'en' ? item.titleEn : item.titleFr;
    const searchMatch = !search || title.toLowerCase().includes(search.toLowerCase());
    const catMatch = category === 'all' || item.category === category;
    return searchMatch && catMatch;
  });

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString(language === 'en' ? 'en-GB' : 'fr-FR', {
      day: 'numeric', month: 'long', year: 'numeric',
    });

  return (
    <Box sx={{ bgcolor: 'background.default' }}>

      {/* ── Hero ── */}
      <PageHero
        title={t.news.title}
        subtitle={t.news.subtitle}
        breadcrumbs={[
          { label: language === 'en' ? 'Home' : 'Accueil', path: '/' },
          { label: t.nav.news },
        ]}
        accentColor="#2E7D32"
      />

      {/* ── Sub-category cards ── */}
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Grid container spacing={2}>
          {subCards.map((card, i) => (
            <Grid item xs={6} sm={3} key={i}>
              <Box sx={{ border: '1px solid', borderColor: 'divider', overflow: 'hidden', height: '100%', cursor: 'pointer', '&:hover': { boxShadow: 3 }, transition: 'box-shadow 0.2s' }}>
                <Box sx={{ height: 160, background: `linear-gradient(135deg, ${card.bgColor}CC 0%, ${card.bgColor}88 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3.5rem' }}>
                  {card.icon}
                </Box>
                <Box sx={{ p: 2 }}>
                  <Box sx={{ borderLeft: '3px solid', borderColor: 'primary.main', pl: 1.5, mb: 1.5 }}>
                    <Typography variant="subtitle1" fontWeight={700} lineHeight={1.3}>
                      {language === 'en' ? card.titleEn : card.titleFr}
                    </Typography>
                  </Box>
                  <Typography onClick={() => setCategory(categories[i] || 'all')}
                    sx={{ color: 'primary.main', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5, '&:hover': { textDecoration: 'underline' } }}>
                    {language === 'en' ? 'Learn more' : 'En savoir plus'} <ArrowForward sx={{ fontSize: 14 }} />
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ── Main content + Sidebar ── */}
      <Container maxWidth="xl" sx={{ pb: 8 }}>
        <Grid container spacing={4}>

          {/* Left — news content */}
          <Grid item xs={12} md={8}>

            <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: '0.04em', mb: 2, fontSize: { xs: '1rem', md: '1.25rem' } }}>
              {language === 'en' ? 'LATEST NEWS & HEALTH INFORMATION' : 'DERNIÈRES ACTUALITÉS & INFORMATIONS SANTÉ'}
            </Typography>
            <Typography variant="body1" color="text.secondary" lineHeight={1.9} mb={3}>
              {language === 'en'
                ? 'Stay informed with the latest news from Hôpital Régional de Bafoussam — from department announcements and medical campaigns to evidence-based health tips relevant to the West Region of Cameroon.'
                : 'Restez informé des dernières actualités de l\'Hôpital Régional de Bafoussam — des annonces de services et campagnes médicales aux conseils santé fondés sur des preuves pertinents pour la Région de l\'Ouest du Cameroun.'}
            </Typography>

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField size="small" placeholder={t.common.search} value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                sx={{ width: { xs: '100%', sm: 260 }, '& .MuiOutlinedInput-root': { borderRadius: 0 } }} />
              <TextField select size="small" value={category} onChange={(e) => setCategory(e.target.value)}
                sx={{ width: { xs: '100%', sm: 200 }, '& .MuiOutlinedInput-root': { borderRadius: 0 } }}>
                <MenuItem value="all">{language === 'en' ? 'All Categories' : 'Toutes les Catégories'}</MenuItem>
                {categories.map((cat) => <MenuItem key={cat} value={cat}>{cat}</MenuItem>)}
              </TextField>
            </Box>

            {/* Category chips */}
            <Stack direction="row" spacing={1} sx={{ mb: 4, flexWrap: 'wrap', gap: 1 }}>
              <Chip label={language === 'en' ? 'All' : 'Tout'} onClick={() => setCategory('all')}
                color={category === 'all' ? 'primary' : 'default'} variant={category === 'all' ? 'filled' : 'outlined'}
                sx={{ borderRadius: 0 }} />
              {categories.map((cat) => (
                <Chip key={cat} label={cat} onClick={() => setCategory(cat)}
                  variant={category === cat ? 'filled' : 'outlined'}
                  sx={category === cat ? { bgcolor: categoryColors[cat], color: 'white', borderRadius: 0 } : { borderRadius: 0 }} />
              ))}
            </Stack>

            {/* Featured article */}
            {filtered.length > 0 && (
              <Card sx={{ mb: 4, cursor: 'pointer', transition: 'all 0.2s', borderRadius: 0, border: '1px solid', borderColor: 'divider', '&:hover': { boxShadow: 6 } }}>
                <Grid container>
                  <Grid item xs={12} md={5}>
                    <Box sx={{ height: { xs: 180, md: '100%' }, minHeight: { md: 240 }, bgcolor: filtered[0].image, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                      <Chip label={language === 'en' ? 'Featured' : 'À la Une'} sx={{ position: 'absolute', top: 16, left: 16, bgcolor: 'white', fontWeight: 700, borderRadius: 0 }} />
                      <MedicalServices sx={{ fontSize: 60, color: 'rgba(255,255,255,0.3)' }} />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <CardContent sx={{ p: { xs: 3, md: 4 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Chip label={filtered[0].category} size="small"
                          sx={{ bgcolor: categoryColors[filtered[0].category] || '#0B4F8A', color: 'white', fontWeight: 700, borderRadius: 0 }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CalendarToday sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">{formatDate(filtered[0].date)}</Typography>
                        </Box>
                      </Box>
                      <Typography variant="h5" fontWeight={700} mb={2} lineHeight={1.4}>
                        {language === 'en' ? filtered[0].titleEn : filtered[0].titleFr}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" lineHeight={1.8} flexGrow={1}>
                        {language === 'en' ? filtered[0].excerptEn : filtered[0].excerptFr}
                      </Typography>
                    </CardContent>
                  </Grid>
                </Grid>
              </Card>
            )}

            {/* Grid */}
            <Grid container spacing={2.5}>
              {filtered.slice(1).map((item) => (
                <Grid item xs={12} sm={6} key={item.id}>
                  <Card sx={{ height: '100%', cursor: 'pointer', borderRadius: 0, border: '1px solid', borderColor: 'divider', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-3px)', boxShadow: 5 } }}>
                    <Box sx={{ height: 140, bgcolor: item.image, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                      <Chip label={item.category} size="small"
                        sx={{ position: 'absolute', top: 12, left: 12, bgcolor: categoryColors[item.category] || 'white', color: 'white', fontWeight: 700, fontSize: '0.72rem', borderRadius: 0 }} />
                      <MedicalServices sx={{ fontSize: 36, color: 'rgba(255,255,255,0.3)' }} />
                    </Box>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <CalendarToday sx={{ fontSize: 13, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">{formatDate(item.date)}</Typography>
                      </Box>
                      <Box sx={{ borderLeft: '3px solid', borderColor: categoryColors[item.category] || 'primary.main', pl: 1.5, mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight={700} lineHeight={1.4}>
                          {language === 'en' ? item.titleEn : item.titleFr}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
                        {language === 'en' ? item.excerptEn : item.excerptFr}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {filtered.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography color="text.secondary">{language === 'en' ? 'No articles found.' : 'Aucun article trouvé.'}</Typography>
              </Box>
            )}
          </Grid>

          {/* Right — sidebar */}
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 0, overflow: 'hidden', position: { md: 'sticky' }, top: { md: 90 } }}>
              <Box sx={{ p: 3, bgcolor: 'primary.main', color: 'white' }}>
                <Typography variant="subtitle1" fontWeight={800} mb={0.5}>
                  {language === 'en' ? 'Health at a Glance' : 'Santé en Bref'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                  {language === 'en' ? 'Key health tips for our community' : 'Conseils santé clés pour notre communauté'}
                </Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                {[
                  { icon: '🦟', en: 'Sleep under insecticide-treated mosquito nets every night to prevent malaria.', fr: 'Dormez chaque nuit sous une moustiquaire imprégnée d\'insecticide pour prévenir le paludisme.' },
                  { icon: '💧', en: 'Always drink boiled or treated water. Waterborne diseases are preventable.', fr: 'Buvez toujours de l\'eau bouillie ou traitée. Les maladies hydriques sont évitables.' },
                  { icon: '🤲', en: 'Wash hands with soap for at least 20 seconds — before eating and after the toilet.', fr: 'Lavez-vous les mains au savon pendant au moins 20 secondes — avant de manger et après les toilettes.' },
                  { icon: '💉', en: 'Keep your children\'s vaccination schedule up to date. Vaccines save lives.', fr: 'Gardez le calendrier vaccinal de vos enfants à jour. Les vaccins sauvent des vies.' },
                  { icon: '🩺', en: 'Attend annual health check-ups even when you feel well. Early detection saves lives.', fr: 'Faites un bilan de santé annuel même lorsque vous vous sentez bien. La détection précoce sauve des vies.' },
                ].map((tip, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                    <Typography sx={{ fontSize: '1.2rem', flexShrink: 0 }}>{tip.icon}</Typography>
                    <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
                      {language === 'en' ? tip.en : tip.fr}
                    </Typography>
                  </Box>
                ))}
                <Divider sx={{ my: 2 }} />
                <Button variant="contained" fullWidth startIcon={<CalendarMonth />}
                  onClick={() => navigate('/appointment')}
                  sx={{ textTransform: 'none', borderRadius: 0, fontWeight: 700 }}>
                  {language === 'en' ? 'Book a Check-up' : 'Prendre un Bilan de Santé'}
                </Button>
              </Box>
            </Paper>

            {/* Quick links */}
            <Box sx={{ mt: 3, p: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
              <Typography variant="subtitle2" fontWeight={700} mb={2} color="primary.main">
                {language === 'en' ? 'Quick Links' : 'Liens Rapides'}
              </Typography>
              {[
                { label: language === 'en' ? 'Emergency Services' : 'Urgences', path: '/emergency' },
                { label: language === 'en' ? 'Our Departments' : 'Nos Services', path: '/departments' },
                { label: language === 'en' ? 'Patient Information' : 'Info Patients', path: '/patients' },
                { label: language === 'en' ? 'Contact Us' : 'Nous Contacter', path: '/contact' },
              ].map((link, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                  onClick={() => navigate(link.path)}>
                  <ArrowForward sx={{ fontSize: 14, color: 'primary.main' }} />
                  <Typography variant="body2" color="text.secondary" sx={{ '&:hover': { color: 'primary.main' } }}>{link.label}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>

        </Grid>
      </Container>
    </Box>
  );
};

export default NewsPage;
